import React, { useState, useContext } from "react";
import { motion } from "framer-motion";
import { useHistory } from "react-router-dom";
import config from "../../../config";
import OtpVerification from "../OtpVerification";
import { useAuth } from "../../../hooks/useAuth";
import { AuthContext } from "../../../Contexts/AuthContext";
import {
  LockClosedIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  CheckCircleIcon
} from "@heroicons/react/24/outline";

const Login = ({ idPrefix = '', embedded = false }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [success, setSuccess] = useState("");
  const history = useHistory();
  const { checkUserLoggedIn } = useContext(AuthContext);
  const { login, verifyOtp } = useAuth();

  const handleEmail = (e) => setEmail(e.target.value);
  const handlePassword = (e) => setPassword(e.target.value);
  const handleRememberMe = (e) => setRememberMe(e.target.checked);
  const handleForgotPassword = () => setForgotPassword(true);

  const sendPasswordResetEmail = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    setIsSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(`${config.backendUrl}/user/reset-password`, {
        method: "POST",
        body: JSON.stringify({ user_email_address: email }),
        headers: { "Content-type": "application/json; charset=UTF-8" },
      });

      const res = await response.json();

      if (!response.ok) {
        throw new Error(res.msg || `Server error: ${response.status}`);
      }

      if (res.success) {
        setForgotPassword(false);
        setSuccess("Password reset email sent. Please check your inbox.");
      } else {
        setError(res.msg || "Error sending password reset email.");
      }
    } catch (error) {
      console.error("Error sending password reset email:", error);
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const result = await login(email, password);

      if (result && result.user_role) {
        await checkUserLoggedIn();
        const userRole = result.user_role;
        // /dashboard and /professor have their routes commented out in App.js
        // (those pages call backend endpoints that do not exist), so the old map
        // dumped every student and teacher straight onto /404 after login.
        const targetPath = { 1: "/admincontrolpanel", 2: "/notepad", 3: "/notepad", 4: "/guest" }[userRole] || "/";
        history.push(targetPath);
      } else if (result && result.needsVerification) {
        setNeedsVerification(true);
      } else {
        setError(result?.error || "Invalid email or password");
      }
    } catch (error) {
      console.error("Login error:", error);
      setError(error.message || "An unexpected error occurred.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpVerificationComplete = async (isSuccess, verificationCode) => {
    if (isVerifying) return; // Prevent double submission

    if (isSuccess) {
      setIsVerifying(true);
      setError("");
      try {
        const result = await verifyOtp(email, verificationCode);
        if (result.success) {
          setSuccess("Verification successful! Logging you in...");
          // Automatically attempt to log in after successful verification
          const loginResult = await login(email, password);
          if (loginResult && loginResult.user_role) {
            await checkUserLoggedIn();
            const userRole = loginResult.user_role;
            const targetPath = { 1: "/admincontrolpanel", 2: "/notepad", 3: "/notepad", 4: "/guest" }[userRole] || "/";
            history.push(targetPath);
          } else {
             setError(loginResult?.error || "Login failed after verification. Please try logging in manually.");
             setNeedsVerification(false); // Go back to login form
          }
        } else {
          setError(result?.error || "Email verification failed. Please try again.");
        }
      } catch (error) {
        setError("An error occurred during verification.");
      } finally {
        setIsVerifying(false);
      }
    } else {
      setError("Invalid OTP format. Please try again.");
    }
  };

  const renderLoginForm = () => (
    <div className="w-full max-w-md" style={{ backgroundColor: "white" }}>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Welcome Back</h2>
        <p className="mt-2 text-sm text-gray-600">Sign in to continue your learning journey</p>
      </div>
      {error && <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded-md text-red-700">{error}</div>}
      {success && <div className="mb-4 bg-green-50 border-l-4 border-green-500 p-4 rounded-md text-green-700">{success}</div>}
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div>
          <label htmlFor={`${idPrefix}login-email-address`} className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
          <input id={`${idPrefix}login-email-address`} name="email" type="email" required className="appearance-none pl-3 block w-full px-3 py-3 border border-gray-300 rounded-lg" placeholder="name@example.com" value={email} onChange={handleEmail} />
        </div>
        <div>
          <label htmlFor={`${idPrefix}login-password`} className="block text-sm font-medium text-gray-700 mb-1">Password</label>
          <input id={`${idPrefix}login-password`} name="password" type="password" required className="appearance-none pl-3 block w-full px-3 py-3 border border-gray-300 rounded-lg" placeholder="••••••••" value={password} onChange={handlePassword} />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input id={`${idPrefix}login-remember-me`} name="remember-me" type="checkbox" className="h-4 w-4 text-indigo-600" checked={rememberMe} onChange={handleRememberMe} />
            <label htmlFor={`${idPrefix}login-remember-me`} className="ml-2 block text-sm text-gray-700">Remember me</label>
          </div>
          <button type="button" onClick={handleForgotPassword} className="font-medium text-indigo-600 hover:text-indigo-800">Forgot password?</button>
        </div>
        <motion.button type="submit" disabled={isSubmitting} className={`w-full flex justify-center py-3 px-4 text-white rounded-lg ${isSubmitting ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'}`} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          {isSubmitting ? 'Signing in...' : 'Sign in'}
        </motion.button>
      </form>
    </div>
  );

  // This form used to post to /user/reset-password, an endpoint the backend has
  // never had, and outbound mail is not configured either — so it could only ever
  // fail. Rather than a form that cannot work, explain what does.
  const renderForgotPasswordForm = () => (
    <div className="w-full max-w-md">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Forgotten your password?</h2>
        <p className="mt-2 text-sm text-gray-600">
          We cannot email a reset link yet. Here is what works.
        </p>
      </div>
      <div className="space-y-4 text-sm text-gray-700">
        <p>
          Still signed in? Open your{" "}
          <button
            type="button"
            onClick={() => history.push("/profile")}
            className="font-medium text-indigo-600 hover:text-indigo-800"
          >
            Account page
          </button>{" "}
          and change it there.
        </p>
        <p>
          Locked out? Contact us from the address on your account and an
          administrator will reset it for you.
        </p>
      </div>
      <div className="mt-6 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setForgotPassword(false)}
          className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
        >
          Back to login
        </button>
        <button
          type="button"
          onClick={() => history.push("/contact")}
          className="rounded-lg bg-indigo-600 px-4 py-2.5 text-white hover:bg-indigo-700"
        >
          Contact us
        </button>
      </div>
    </div>
  );

  const content = needsVerification ? (
    <OtpVerification
      email={email}
      message="An activation code was sent to your email. Please enter it below."
      onComplete={handleOtpVerificationComplete}
    />
  ) : forgotPassword ? (
    renderForgotPasswordForm()
  ) : (
    renderLoginForm()
  );

  // Embedded means the login/register page already supplies the card and the
  // page shell. Rendering this component's own min-h-screen + card inside that
  // nested it two cards deep on phones and pushed the sign-in button below the
  // fold, so skip the shell here.
  if (embedded) return content;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-md p-6 bg-white rounded-xl shadow-md">
        {content}
      </div>
    </div>
  );
};

export default Login;